/**
 * Fleet domain models for משימות שינוע 99 — vehicles, soldiers (drivers) and
 * the result shape of the driver/vehicle matching engine.
 *
 * These types are the single source of truth for the fleet layer: the matcher
 * (`src/core/driver-matcher.ts`), the offline store (`src/db/fleet-store.ts`)
 * and the verification fixtures (`src/core/mock-fleet.ts`) all build on the
 * shapes here. Nothing seeds a vehicle or a soldier — the roster starts empty.
 */
import type { AuditedEntity, ISODateString } from './index';

// ── Licensing ────────────────────────────────────────────────────────

/**
 * Military licensing weight classes, ordered from the lightest to the
 * heaviest. The scale is cumulative: a licence for a given class also covers
 * every class below it.
 */
export const WEIGHT_CATEGORIES = ['light', 'medium', 'heavy', 'articulated'] as const;

export type WeightCategory = (typeof WEIGHT_CATEGORIES)[number];

export const WEIGHT_CATEGORY_LABELS: Record<WeightCategory, string> = {
  light: 'קל — עד 3.5 טון',
  medium: 'בינוני — 3.5 עד 12 טון',
  heavy: 'כבד — מעל 12 טון',
  articulated: 'מפרקי — סמיטריילר',
};

/**
 * The class on its own, without the tonnage — for a line that has no room for
 * the full sentence, such as a crew row on the portrait dispatch card. Derived
 * from {@link WEIGHT_CATEGORY_LABELS}, so the wording is still written once.
 */
export const WEIGHT_CATEGORY_SHORT_LABELS: Record<WeightCategory, string> = Object.fromEntries(
  WEIGHT_CATEGORIES.map((category) => [category, WEIGHT_CATEGORY_LABELS[category].split('—')[0].trim()]),
) as Record<WeightCategory, string>;

/** Position of a class on the cumulative scale (1 = lightest). */
export const WEIGHT_CATEGORY_RANK: Record<WeightCategory, number> = {
  light: 1,
  medium: 2,
  heavy: 3,
  articulated: 4,
};

/**
 * Special operating endorsements (הרשאות) that can be written on a military
 * driving licence. They are informational only — a record of what the licence
 * carries — and no longer take part in the assignment decision: a vehicle is
 * matched to a driver by the licence weight class alone.
 *
 * Neither screen edits them: the vehicle form no longer asks for required
 * permits and the soldier form never did, so `Soldier.licensePermits` is
 * carried by imported and restored records alone and a soldier added by hand
 * starts with an empty list.
 */
export const VEHICLE_PERMITS = [
  'crane',
  'hazmat',
  'trailer',
  'off_road',
  'night',
  'convoy',
] as const;

export type VehiclePermit = (typeof VEHICLE_PERMITS)[number];

export const VEHICLE_PERMIT_LABELS: Record<VehiclePermit, string> = {
  crane: 'הפעלת מנוף',
  hazmat: 'הובלת חומרים מסוכנים',
  trailer: 'גרירת נגרר',
  off_road: 'נהיגת שטח',
  night: 'נהיגת לילה',
  convoy: 'הובלה בשיירה',
};

// ── Vehicles ─────────────────────────────────────────────────────────

export const VEHICLE_TYPES = [
  'van',
  'truck',
  'flatbed',
  'tanker',
  'crane_truck',
  'semitrailer',
  'bus',
] as const;

export type VehicleType = (typeof VEHICLE_TYPES)[number];

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  van: 'טנדר',
  truck: 'משאית',
  flatbed: 'משאית משטח',
  tanker: 'מכלית',
  crane_truck: 'משאית מנוף',
  semitrailer: 'סמיטריילר',
  bus: 'אוטובוס',
};

/**
 * Fleet category, the way the unit itself splits its vehicles: `yaram` is the
 * operational pool (צ' ירם), `brown` the administrative one (צ' חום). It is a
 * filing distinction, not a licensing one — the matcher ignores it.
 */
export const VEHICLE_CATEGORIES = ['yaram', 'brown'] as const;

export type VehicleCategory = (typeof VEHICLE_CATEGORIES)[number];

export const VEHICLE_CATEGORY_LABELS: Record<VehicleCategory, string> = {
  yaram: 'ירם',
  brown: 'חום',
};

export const VEHICLE_CATEGORY_DESCRIPTIONS: Record<VehicleCategory, string> = {
  yaram: 'מאגר מבצעי — רכבי ירם',
  brown: 'מאגר מנהלה — רכבי חום',
};

export const VEHICLE_STATUSES = ['available', 'in_use', 'maintenance', 'grounded'] as const;

export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  available: 'זמין',
  in_use: 'במשימה',
  maintenance: 'בטיפול',
  grounded: 'מושבת',
};

/**
 * A vehicle in the pool. It extends {@link AuditedEntity}, so every row says who
 * opened it and who last moved its status — the מצב תפעולי and the holding
 * company are the two things the unit changes hourly, and both come through the
 * one write path in `src/db/fleet-store.ts` that stamps the name.
 */
export interface Vehicle extends AuditedEntity {
  /**
   * Three-digit internal number painted on the vehicle (מספר פנימי), or an
   * empty string when the unit has not recorded one yet — the pool the company
   * handed over came with plates alone. Where it *is* recorded it is unique,
   * which `src/db/fleet-store.ts` enforces.
   */
  militaryId: string;
  /** Full military plate as written on the licence, e.g. `צ 12-345-67`. */
  plateNumber: string;
  /** The name the vehicle is called by on the net, e.g. `אקטרוס פלוגתי`. */
  name: string;
  model: string;
  vehicleType: VehicleType;
  /** Which pool the vehicle belongs to — ירם or חום. */
  category: VehicleCategory;
  /**
   * The licence class a driver must hold, at minimum — and the only licensing
   * demand a vehicle makes. Operating endorsements are not required of it.
   */
  weightCategory: WeightCategory;
  status: VehicleStatus;
  /**
   * Command-level block list. A soldier listed here may never be assigned to
   * this vehicle, no matter how complete their licence is.
   */
  disallowedSoldierIds: string[];
  /** The company that owns the vehicle. It does not move. */
  company: string;
  /**
   * The company physically holding the vehicle right now — `שינוע` while it is
   * with us, another unit's name while it is on loan. Blank or missing reads as
   * home, so a record written before loans existed needs no migration.
   *
   * This is the only field a transfer writes; the green / amber / red badge is
   * derived from it — see `src/core/vehicle-location.ts`.
   */
  assignedCompany?: string;
  notes?: string;
}

// ── Soldiers (drivers) ───────────────────────────────────────────────

export const SOLDIER_AVAILABILITIES = ['available', 'on_mission', 'leave', 'unavailable'] as const;

export type SoldierAvailability = (typeof SOLDIER_AVAILABILITIES)[number];

export const SOLDIER_AVAILABILITY_LABELS: Record<SoldierAvailability, string> = {
  available: 'זמין',
  on_mission: 'במשימה',
  leave: 'בחופשה',
  unavailable: 'לא זמין',
};

/**
 * The six jobs the company actually holds — and nothing else. Drives the roster
 * filters and the order the roster is read in, never the licence rules: a מ"פ
 * with a C licence is matched to a truck exactly as a נהג is.
 *
 * The list is written from the top of the chain of command down, and that order
 * *is* the hierarchy — {@link SOLDIER_ROLE_RANK} is derived from it, and every
 * dropdown offers the roles in the same order. There is one list to keep, not a
 * list and a ranking beside it.
 *
 * Everything that has to reason about a role — the rank, the label a stored
 * record reads as, the fallback for a job the roster no longer offers — lives in
 * `src/core/soldier-roles.ts`.
 */
export const SOLDIER_ROLES = [
  'company_commander',
  'deputy_commander',
  'clerk',
  'vehicle_officer',
  'sergeant_major',
  'driver',
] as const;

export type SoldierRole = (typeof SOLDIER_ROLES)[number];

export const SOLDIER_ROLE_LABELS: Record<SoldierRole, string> = {
  company_commander: 'מ"פ',
  deputy_commander: 'סמ"פ',
  clerk: 'פקיד/ה',
  vehicle_officer: 'קצין רכב',
  sergeant_major: 'רס"פ',
  driver: 'נהג',
};

/**
 * The job a soldier is filed under when nobody has said otherwise — an
 * unassigned record, or one an older build filed under a job the roster no
 * longer keeps. The company is a transport company: its default is נהג.
 */
export const DEFAULT_SOLDIER_ROLE: SoldierRole = 'driver';

/**
 * Position in the chain of command — `1` is the מ"פ, `6` the נהג. Derived from
 * {@link SOLDIER_ROLES}, so the order the list is written in is the only place
 * the hierarchy is stated.
 */
export const SOLDIER_ROLE_RANK: Record<SoldierRole, number> = Object.fromEntries(
  SOLDIER_ROLES.map((role, index) => [role, index + 1]),
) as Record<SoldierRole, number>;

/**
 * Where a soldier is while they are away, for a status that expires by itself
 * ("בבית עד יום שלישי 08:00"). The kind carries a Hebrew label; the
 * availability it implies lives in {@link TIMED_STATUS_AVAILABILITY}.
 */
export const TIMED_STATUS_KINDS = [
  'home',
  'leave',
  'sick',
  'course',
  'guard',
  'mission',
] as const;

export type TimedStatusKind = (typeof TIMED_STATUS_KINDS)[number];

export const TIMED_STATUS_LABELS: Record<TimedStatusKind, string> = {
  home: 'בבית',
  leave: 'בחופשה',
  sick: 'בגימלים',
  course: 'בקורס',
  guard: 'בשמירה',
  mission: 'במשימה',
};

/** The availability a timed status forces while it is still running. */
export const TIMED_STATUS_AVAILABILITY: Record<TimedStatusKind, SoldierAvailability> = {
  home: 'unavailable',
  leave: 'leave',
  sick: 'unavailable',
  course: 'unavailable',
  guard: 'unavailable',
  mission: 'on_mission',
};

/**
 * Where a soldier is while they are away, with or without an end in sight.
 *
 * With an `until`, the status ends on its own: once the moment has passed the
 * soldier falls back to the availability stored on their record and nothing
 * has to run. `until: null` is the open-ended case — בגימלים with no date on
 * it — and stays in force until a commander changes it by hand. The deadline
 * is a convenience, not a condition: the field commander does not always know
 * when the soldier is back, and refusing the status until they invent a date
 * is how a roster ends up lying.
 */
export interface TimedStatus {
  kind: TimedStatusKind;
  /** The moment the status lapses, or `null` for an open-ended one. */
  until: ISODateString | null;
  /** Free-text addition from the manager, e.g. `אישור מ"פ`. */
  note?: string;
}

/**
 * A soldier on the roster. It extends {@link AuditedEntity} for the same reason
 * a vehicle does: a status ("בבית עד יום שלישי") is set from a phone in the
 * field, and the next commander to read the roster has to know whose word it is.
 *
 * A soldier is a *record*, not an account: `userId` links to one where the
 * person is also an operator, and a נהג has none — drivers never sign in, they
 * receive their פקודת משימה as an image. See `src/core/operator-access.ts`.
 */
export interface Soldier extends AuditedEntity {
  /** Links to the auth `User` record when the soldier has an account. */
  userId?: string;
  fullName: string;
  /**
   * Military personal number (as on the `User` record) — the soldier's only
   * identifying number. The three-digit internal number (מספר פנימי) belongs
   * to vehicles alone and is not carried here.
   */
  personalNumber: string;
  role: SoldierRole;
  company: string;
  platoon: string;
  phone: string;
  /** Highest weight class the military driving licence covers. */
  licenseWeightCategory: WeightCategory | null;
  /**
   * Endorsements written on the licence, shown on the roster for information.
   * They are not edited from the roster screens — a hand-added soldier starts
   * with an empty list — and the matcher does not read them, but a record that
   * arrives from a backup keeps whatever it came with.
   */
  licensePermits: VehiclePermit[];
  licenseExpiresAt: ISODateString | null;
  availability: SoldierAvailability;
  /**
   * Temporary whereabouts that expire by themselves. While it is running it
   * overrides `availability` for display and for the roster filters.
   */
  temporaryStatus?: TimedStatus | null;
  /**
   * Command-level block list, mirroring `Vehicle.disallowedSoldierIds`.
   * Either side of the pair is enough to block the assignment.
   */
  disallowedVehicleIds: string[];
  /** Manager notes, shown on the soldier card details view. */
  notes?: string;
}

// ── Matching engine result ───────────────────────────────────────────

/**
 * Every reason an assignment can be refused, in precedence order — the first
 * entry that applies becomes `DriverVehicleMatch.reason`.
 */
export const MATCH_FAILURE_CODES = [
  'EXPLICIT_DISALLOW',
  'NO_LICENSE',
  'LICENSE_EXPIRED',
  'MISSING_WEIGHT_CATEGORY',
  'SOLDIER_UNAVAILABLE',
  'VEHICLE_UNAVAILABLE',
] as const;

export type MatchFailureCode = (typeof MATCH_FAILURE_CODES)[number];

export const MATCH_FAILURE_LABELS: Record<MatchFailureCode, string> = {
  EXPLICIT_DISALLOW: 'החייל מוחרג מפורשות מרכב זה',
  NO_LICENSE: 'לחייל אין רישיון נהיגה צבאי',
  LICENSE_EXPIRED: 'רישיון הנהיגה פג תוקף',
  MISSING_WEIGHT_CATEGORY: 'דרגת המשקל ברישיון אינה מכסה את הרכב',
  SOLDIER_UNAVAILABLE: 'החייל אינו זמין',
  VEHICLE_UNAVAILABLE: 'הרכב אינו זמין',
};

export interface MatchFailure {
  code: MatchFailureCode;
  /** Hebrew explanation, ready for the UI. */
  message: string;
}

export interface DriverVehicleMatch {
  ok: boolean;
  soldierId: string;
  vehicleId: string;
  /** Highest-precedence blocking reason, or `null` on a match. */
  reason: MatchFailureCode | null;
  /** Every reason that applies, in precedence order. */
  failures: MatchFailure[];
  /** The vehicle's class, when the licence does not reach it. */
  missingWeightCategory: WeightCategory | null;
  /** 0–100 fitness score for ranking candidates; always 0 when blocked. */
  score: number;
}
